// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
/**
 * Dado de leitura do Ateste de Medição, resolvido a partir de
 * `dados-simulados.json`.
 *
 * NENHUMA DATA LITERAL. O JSON guarda a distância em meses até hoje
 * (`competenciaAtras`) e, dentro da competência, só o dia do mês
 * (`diaOperacao`, `calculadoEm.dia`). Este arquivo monta as datas completas: ano-mês
 * escrito à mão envelhece sozinho, e um dia a tela mostraria à SMT uma
 * competência que já passou. `dadosSimulados.spec.ts` (módulo financeiro)
 * falha se alguém escrever um ano-mês no JSON.
 */

import DADOS_SIMULADOS from './dados-simulados.json';
import type {
  CredenciadoAteste,
  DiaApontamento,
  DreAteste,
  FixtureAteste,
  OrdemServicoAteste,
} from './tipos';

/** `AAAA-MM` deslocado `mesesAtras` meses a partir do mês corrente. */
export function anoMesAtras(mesesAtras: number): string {
  const hoje = new Date();
  const data = new Date(hoje.getFullYear(), hoje.getMonth() - mesesAtras, 1);
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
}

/** Fuso de São Paulo, o das datas e horas que a tela exibe. */
const FUSO_DE_SAO_PAULO = '-03:00';

interface DiaBruto extends Omit<DiaApontamento, 'dataOperacao'> {
  diaOperacao: number;
}

interface OrdemServicoBruta extends Omit<OrdemServicoAteste, 'dias'> {
  dias: DiaBruto[];
}

interface CredenciadoBruto extends Omit<CredenciadoAteste, 'calculadoEm' | 'ordensServico'> {
  /** Dia do mês da competência e hora local (`HH:MM:SS`); `null` = ainda não calculada. */
  calculadoEm: { dia: number; hora: string } | null;
  ordensServico: OrdemServicoBruta[];
}

interface DreBruta extends Omit<DreAteste, 'credenciados'> {
  credenciados: CredenciadoBruto[];
}

interface FixtureBruta extends Omit<FixtureAteste, 'competencia' | 'dres'> {
  competenciaAtras: number;
  dres: DreBruta[];
}

function doisDigitos(n: number): string {
  return String(n).padStart(2, '0');
}

function resolver(bruta: FixtureBruta): FixtureAteste {
  const anoMes = anoMesAtras(bruta.competenciaAtras);
  return {
    competencia: `${anoMes}-01`,
    alertaTabelaAlterada: bruta.alertaTabelaAlterada,
    config: bruta.config,
    dres: bruta.dres.map((dre) => ({
      ...dre,
      credenciados: dre.credenciados.map(({ calculadoEm, ordensServico, ...credenciado }) => ({
        ...credenciado,
        calculadoEm: calculadoEm
          ? `${anoMes}-${doisDigitos(calculadoEm.dia)}T${calculadoEm.hora}${FUSO_DE_SAO_PAULO}`
          : null,
        ordensServico: ordensServico.map(({ dias, ...os }) => ({
          ...os,
          dias: dias.map(({ diaOperacao, ...dia }) => ({
            ...dia,
            dataOperacao: `${anoMes}-${doisDigitos(diaOperacao)}`,
          })),
        })),
      })),
    })),
  };
}

export const FIXTURE_ATESTE: FixtureAteste = resolver(DADOS_SIMULADOS as FixtureBruta);
